// src/pages/JudoScorePro/JudoScorePro.jsx (adapter le chemin si besoin)
import React, { useState } from "react";
import { FaArrowRight, FaArrowLeft, FaPlay, FaClock } from "react-icons/fa";
import GooglePlayLogo from "./Google-Play-Logo.png";
import AppleStoreLogo from "./apple_store.png";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import { useNavigate } from "react-router-dom";
import ScrollIndicator from "./../../hooks/ScrollIndicator";

const images = [
  {
    src: "/images/judoscorepro/judoscore01.png",
    alt: "Capture 1",
    caption: "Tableau de score",
  },
  {
    src: "/images/judoscorepro/judoscore02.png",
    alt: "Capture 2",
    caption: "Mode d'emploi",
  },
  {
    src: "/images/judoscorepro/judoscore03.png",
    alt: "Capture 3",
    caption: "Réglages",
  },
  {
    src: "/images/judoscorepro/judoscore04.png",
    alt: "Capture 4",
    caption: "Sélection des langues",
  },
];

// helper pour générer un petit ID aléatoire
const createRandomId = () => {
  return (
    Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4)
  );
};

// renvoie un "sessionId" unique pour le tableau de score
const getScoreboardSessionId = (user) => {
  // Cas 1 : utilisateur connecté → session liée à son userId
  if (user?.userId) {
    return `u-${user.userId}`;
  }

  // Cas 2 : visiteur → on regarde si on a déjà un id en localStorage
  let stored = null;
  try {
    stored = localStorage.getItem("scoreboard_guest_id");
  } catch {
    // si localStorage HS (mode privé extrême), on s'en fout, on en régénère un
  }

  if (!stored) {
    const newId = `g-${createRandomId()}`;
    try {
      localStorage.setItem("scoreboard_guest_id", newId);
    } catch {
      // ignore
    }
    return newId;
  }

  return stored;
};

const JudoScorePro = () => {
  useScrollToTop();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id); // gardé si tu veux l’utiliser plus tard
  const navigate = useNavigate();

  const handleOpenScoreboard = () => {
    const sessionId = getScoreboardSessionId(user);
    navigate(`/tableau-de-score/${sessionId}`);
  };

  const handleNext = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
  };

  const { src, alt, caption } = images[currentImageIndex];

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      {/* Header */}
      <div className="text-center space-y-2 mb-8">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Visiteurs
        </h2>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          Tableau de Score de Judo
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
          Application de Tableau de Score de Judo pour les clubs, compétitions
          et animations. Disponible sur cette application gratuitement ainsi que
          les version Android et iOS.
        </p>
      </div>

      {/* --- NOUVEL ENCARt : ACCÈS GRATUIT AU TABLEAU DE SCORE WEB --- */}
      <div className="max-w-[1280px] mx-auto mt-10 px-2 sm:px-4">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-700 to-sky-500 text-white shadow-2xl border border-white/10">
          {/* Glow / décor */}
          <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_top,_#ffffff,transparent_55%)] pointer-events-none" />
          <div className="absolute -right-16 -bottom-16 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

          <div className="relative p-6 md:p-8 lg:p-10 flex flex-col lg:flex-row items-center gap-8 lg:gap-10">
            {/* Texte à gauche */}
            <div className="w-full lg:w-1/2 space-y-4">
              <p className="text-[11px] uppercase tracking-[0.3em] text-white/70">
                Nouveauté MonAppliTournoi
              </p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold leading-tight">
                Accès gratuit au tableau de score en ligne
              </h2>
              <p className="text-sm md:text-base text-white/90">
                Vous pouvez désormais utiliser le tableau de score de judo
                directement depuis MonAppliTournoi, sans créer de compétition ni
                gérer de distributions. Un simple affichage :{" "}
                <strong>Judoka 1 (rouge)</strong> vs{" "}
                <strong>Judoka 2 (blanc)</strong>, idéal pour les animations,
                stages, randoris ou petits challenges internes au club.
              </p>

              <div className="flex flex-wrap gap-3 mt-3">
                <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold inline-flex items-center gap-2">
                  <FaClock className="text-xs" />
                  Temps, Golden Score, Osaekomi, valeurs de score, ...
                </span>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                {/* CTA principal : accès au tableau de score interne */}
                <button
                  type="button"
                  onClick={handleOpenScoreboard}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-blue-700 font-semibold text-sm md:text-base shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-transform"
                >
                  Ouvrir le tableau de score
                  <FaArrowRight className="text-xs" />
                </button>

                {/* Lien vers tutoriel interne (adapter le path si besoin) */}
                <button
                  type="button"
                  onClick={() => handleNavigation("/aide")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/70 text-xs md:text-sm font-medium hover:bg-white/10 transition-colors"
                >
                  Voir le tutoriel du tableau de score
                </button>
              </div>

              <p className="mt-3 text-xs md:text-sm text-white/80">
                Accessible à tous les visiteurs, sans inscription. Il vous
                suffit d&apos;ouvrir la page sur un écran dédié et de piloter le
                tableau depuis votre ordinateur ou tablette.
              </p>
            </div>

            {/* Illustration à droite */}
            <div className="w-full lg:w-1/2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-3 bg-white/25 blur-xl opacity-40" />
                <div className="relative rounded-2xl border border-white/40 bg-slate-900/80 shadow-2xl p-2 sm:p-3  w-full transform hover:-translate-y-1 hover:rotate-1 transition duration-500">
                  <img
                    src="/img/score_public.png"
                    alt="Aperçu du tableau de score"
                    className="rounded-xl w-full h-auto object-contain"
                  />
                  <div className="mt-3 px-2 pb-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/60">
                      Aperçu
                    </p>
                    <p className="text-sm font-semibold">
                      Tableau de score Judo · Mode visiteurs
                    </p>
                    <p className="text-xs text-white/70 mt-1">
                      Ippon, Waza-ari, Shido, Hansoku-make, Osaekomi, Golden
                      Score… tout est déjà prêt.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logo + tagline */}
      <div className="mx-auto max-w-screen-md text-center mb-4 lg:mb-4 flex flex-col sm:flex-row items-center justify-center p-4 gap-4 sm:gap-8">
        {/* Logo cliquable vers Google Play */}
        <a
          href="https://play.google.com/store/apps/details?id=com.victorin.judoscorepro"
          target="_blank"
          rel="noopener noreferrer"
          className="relative inline-flex items-center justify-center"
        >
          <div className="absolute -inset-2 bg-blue-500/20 dark:bg-blue-400/20 rounded-3xl blur-xl opacity-60" />
          <img
            src="/images/judoscorepro/logo_scoreboard_judo.png"
            alt="Logo JudoScore Pro"
            className="relative cursor-pointer rounded-3xl shadow-xl border border-white/40 dark:border-slate-700 bg-white/60 dark:bg-slate-800/80 p-2"
          />
        </a>
        <div className="text-left">
          <p className="text-lg italic mt-2">
            Une application Android et iOS de tableau de score de judo, pensée
            pour les clubs, les animations et les compétitions.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Et désormais, un tableau de score en ligne accessible gratuitement
            pour les visiteurs de MonAppliTournoi.
          </p>
        </div>
      </div>

      {/* Bloc principal : carousel + texte descriptif */}
      <div className="flex flex-col md:flex-row items-center md:items-start max-w-[1280px] mx-auto p-4 gap-10">
        {/* Carrousel */}
        <div className="md:w-1/2 w-full relative flex flex-col items-center justify-center mb-8 md:mb-0">
          <button
            className="absolute left-3 sm:left-4 z-10 bg-white/90 dark:bg-slate-800 text-blue-600 dark:text-blue-400 p-2 rounded-full shadow-lg focus:outline-none hover:scale-105 transition-transform"
            onClick={handlePrev}
          >
            <FaArrowLeft className="text-xl" />
          </button>

          <div className="w-full max-w-4xl mx-auto flex justify-center">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              <img
                key={src}
                src={src}
                alt={alt}
                className="max-h-[600px] w-auto rounded-3xl object-contain transition-transform duration-500 ease-out hover:scale-105"
              />
            </div>
          </div>

          <p className="text-center text-gray-600 dark:text-gray-400 mt-4">
            {caption}
          </p>

          {/* Petits bullets de progression */}
          <div className="flex justify-center gap-2 mt-3">
            {images.map((img, idx) => (
              <button
                key={img.alt}
                onClick={() => setCurrentImageIndex(idx)}
                className={`h-2.5 rounded-full transition-all ${
                  idx === currentImageIndex
                    ? "w-6 bg-blue-600 dark:bg-blue-400"
                    : "w-2.5 bg-gray-300 dark:bg-slate-600"
                }`}
                aria-label={`Aller à la capture ${idx + 1}`}
              />
            ))}
          </div>

          <button
            className="absolute right-3 sm:right-4 z-10 bg-white/90 dark:bg-slate-800 text-blue-600 dark:text-blue-400 p-2 rounded-full shadow-lg focus:outline-none hover:scale-105 transition-transform"
            onClick={handleNext}
          >
            <FaArrowRight className="text-xl" />
          </button>
        </div>

        {/* Texte descriptif à droite */}
        <div className="md:w-1/2 md:pl-4 lg:pl-8 text-left md:text-left">
          <h3 className="text-2xl md:text-3xl font-semibold mb-4">
            Judo Score Pro
          </h3>
          <hr className="border-gray-300 dark:border-gray-700" />
          <p className="mt-4 text-sm md:text-base leading-relaxed">
            JudoScore Pro vous permet de suivre et d&apos;arbitrer les combats
            de judo en temps réel avec un tableau de score interactif. Idéal
            pour les compétitions et animations, l&apos;application simplifie la
            gestion des scores, des chronomètres et la désignation des
            vainqueurs.
          </p>
          <ul className="list-disc list-inside mt-4 space-y-1 text-sm md:text-base">
            <li>
              Tableau de score intuitif pour ajouter ou retirer des points et
              pénalités.
            </li>
            <li>Chronomètres personnalisables avec alertes sonores.</li>
            <li>
              Sélection de catégories de poids et d&apos;âges pour les tirages
              au sort par équipe.
            </li>
            <li>
              Réglages complets : temps de combat, langue, sons, Golden Score,
              Wazari Awasete Ippon, etc.
            </li>
          </ul>
          <p className="mt-4 text-sm md:text-base">
            Découvrez une solution simple et efficace pour gérer vos combats de
            judo avec JudoScore Pro.
          </p>

          <p className="mt-4 text-sm md:text-base">
            L&apos;application est gratuite sur Android. Elle est également
            disponible sur l&apos;Apple Store au prix de 1,99€.
          </p>

          {/* Boutons contribution + stores */}
          <div className="mt-8 flex flex-col md:flex-row justify-center md:justify-start items-center gap-4">
            <a
              href="https://buy.stripe.com/6oEg1hajughs3Kw144"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-full transition shadow-lg inline-flex items-center gap-2"
            >
              Contribution 5€
              <FaArrowRight className="text-xs" />
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.victorin.judoscorepro"
              target="_blank"
              rel="noopener noreferrer"
              className="flex justify-center"
            >
              <img
                src={GooglePlayLogo}
                alt="Disponible sur Google Play"
                className="h-12 w-auto md:h-16"
              />
            </a>

            <a
              href="https://apps.apple.com/fr/app/judoscore-pro/id6739465277"
              target="_blank"
              rel="noopener noreferrer"
              className="flex justify-center"
            >
              <img
                src={AppleStoreLogo}
                alt="Disponible sur l'Apple Store"
                className="h-12 w-auto md:h-16"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JudoScorePro;
